import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  MicrophoneIcon,
  ClipboardDocumentListIcon,
  ShoppingCartIcon,
  UserGroupIcon,
  StarIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { RootState, AppDispatch } from '../store/store';
import { fetchMenuItems } from '../store/slices/menuSlice';

const HomePage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { popularItems } = useSelector((state: RootState) => state.menu);
  const { isAuthenticated, user, guestUser } = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    dispatch(fetchMenuItems({}));
  }, [dispatch]);

  const currentUser = user || guestUser;

  const features = [
    {
      icon: MicrophoneIcon,
      title: 'Voice Ordering',
      description: 'Order with your voice - fast, easy, and hands-free',
      link: '/voice-order',
      color: 'bg-green-500',
    },
    {
      icon: ClipboardDocumentListIcon,
      title: 'Browse Menu',
      description: 'Explore our delicious menu items and categories',
      link: '/menu',
      color: 'bg-blue-500',
    },
    {
      icon: ShoppingCartIcon,
      title: 'Quick Order',
      description: 'Traditional ordering with cart and checkout',
      link: '/menu',
      color: 'bg-purple-500',
    },
    {
      icon: UserGroupIcon,
      title: 'Table Service',
      description: 'Scan QR code for table-specific ordering',
      link: '/table-order',
      color: 'bg-orange-500',
    },
  ];

  const testimonials = [
    {
      name: 'Sarah Chen',
      rating: 5,
      comment: 'The voice ordering is amazing! So convenient and accurate.',
    },
    {
      name: 'Mike Johnson',
      rating: 5,
      comment: 'Love how fast I can place orders. Perfect for busy lunch breaks.',
    },
    {
      name: 'Emily Rodriguez',
      rating: 5,
      comment: 'The AI understands exactly what I want. Revolutionary!',
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section
        className="text-white theme-header"
        style={{
          background: `linear-gradient(135deg, var(--color-primary-500) 0%, var(--color-primary-600) 50%, var(--color-primary-700) 100%)`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              🎤 Voice Restaurant
            </h1>
            <p
              className="text-xl md:text-2xl mb-8"
              style={{ color: 'rgba(255, 255, 255, 0.9)' }}
            >
              Experience the future of dining with AI-powered voice ordering
            </p>

            {isAuthenticated ? (
              <div className="mb-8">
                <h2 className="text-2xl mb-4">
                  Welcome back, {(currentUser && 'firstName' in currentUser) ? currentUser.firstName : currentUser?.name || 'Guest'}! 👋
                </h2>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link
                    to="/voice-order"
                    className="text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors flex items-center justify-center"
                    style={{
                      backgroundColor: 'var(--color-accent)',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-success)'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--color-accent)'}
                  >
                    <MicrophoneIcon className="w-6 h-6 mr-2" />
                    Start Voice Order
                  </Link>
                  <Link
                    to="/menu"
                    className="bg-white hover:bg-gray-100 px-8 py-4 rounded-lg text-lg font-semibold transition-colors"
                    style={{ color: 'var(--color-primary-600)' }}
                  >
                    Browse Menu
                  </Link>
                </div>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                <Link
                  to="/voice-order"
                  className="text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors flex items-center justify-center"
                  style={{
                    backgroundColor: 'var(--color-accent)',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-success)'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--color-accent)'}
                >
                  <MicrophoneIcon className="w-6 h-6 mr-2" />
                  Try Voice Ordering
                </Link>
                <Link
                  to="/register"
                  className="bg-white hover:bg-gray-100 px-8 py-4 rounded-lg text-lg font-semibold transition-colors"
                  style={{ color: 'var(--color-primary-600)' }}
                >
                  Sign Up Free
                </Link>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
              <div className="bg-white/10 rounded-lg p-4">
                <ClockIcon className="w-8 h-8 mx-auto mb-2" />
                <h3 className="font-semibold">30 Seconds</h3>
                <p style={{ color: 'rgba(255, 255, 255, 0.8)' }}>Average order time</p>
              </div>
              <div className="bg-white/10 rounded-lg p-4">
                <StarIcon className="w-8 h-8 mx-auto mb-2" />
                <h3 className="font-semibold">99% Accuracy</h3>
                <p style={{ color: 'rgba(255, 255, 255, 0.8)' }}>Voice recognition</p>
              </div>
              <div className="bg-white/10 rounded-lg p-4">
                <UserGroupIcon className="w-8 h-8 mx-auto mb-2" />
                <h3 className="font-semibold">10,000+</h3>
                <p style={{ color: 'rgba(255, 255, 255, 0.8)' }}>Happy customers</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              How It Works
            </h2>
            <p className="text-xl text-gray-600">
              Choose your preferred way to order
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <Link
                key={index}
                to={feature.link}
                className="bg-white rounded-lg p-6 shadow-lg hover:shadow-xl transition-shadow group"
              >
                <div className={`w-12 h-12 ${feature.color} rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <feature.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600">{feature.description}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Items */}
      {popularItems.length > 0 && (
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Featured Items
              </h2>
              <p className="text-xl text-gray-600">
                Customer favorites and chef recommendations
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {popularItems.slice(0, 3).map((item) => (
                <div
                  key={item.itemCode}
                  className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
                >
                  {item.image && (
                    <img
                      src={item.image}
                      alt={item.itemName}
                      className="w-full h-48 object-cover"
                    />
                  )}
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      {item.itemName}
                    </h3>
                    <p className="text-gray-600 mb-4">{item.description}</p>
                    <div className="flex items-center justify-between">
                      <span
                        className="text-2xl font-bold"
                        style={{ color: 'var(--color-primary-600)' }}
                      >
                        ${item.standardRate}
                      </span>
                      <Link
                        to={`/menu?item=${item.itemCode}`}
                        className="text-white px-4 py-2 rounded-lg transition-colors"
                        style={{ backgroundColor: 'var(--color-primary-500)' }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-primary-600)'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--color-primary-500)'}
                      >
                        Order Now
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mt-8">
              <Link
                to="/menu"
                className="bg-gray-100 hover:bg-gray-200 text-gray-900 px-8 py-3 rounded-lg font-semibold transition-colors"
              >
                View Full Menu
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              What Our Customers Say
            </h2>
            <p className="text-xl text-gray-600">
              Real feedback from real people
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-white rounded-lg p-6 shadow-lg"
              >
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <StarIcon key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-600 mb-4 italic">
                  "{testimonial.comment}"
                </p>
                <p className="font-semibold text-gray-900">
                  {testimonial.name}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        className="text-white py-20"
        style={{ backgroundColor: 'var(--color-primary-500)' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-4">
            Ready to Experience Voice Ordering?
          </h2>
          <p
            className="text-xl mb-8"
            style={{ color: 'rgba(255, 255, 255, 0.8)' }}
          >
            Join thousands of satisfied customers today
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/voice-order"
              className="text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors flex items-center justify-center"
              style={{ backgroundColor: 'var(--color-accent)' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-success)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--color-accent)'}
            >
              <MicrophoneIcon className="w-6 h-6 mr-2" />
              Start Ordering Now
            </Link>
            {!isAuthenticated && (
              <Link
                to="/register"
                className="bg-white hover:bg-gray-100 px-8 py-4 rounded-lg text-lg font-semibold transition-colors"
                style={{ color: 'var(--color-primary-600)' }}
              >
                Create Free Account
              </Link>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;